import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';

// KTS Detail Form Component
const KTSDetailForm = ({ report }) => {
  const data = report.rawData;
  
  // Helper function to format decimal values
  const formatDecimalValue = (value) => {
    if (!value) return '0';
    const num = parseFloat(value);
    return isNaN(num) ? '0' : num.toString();
  };
  
  return (
    <FormContent>
      <FormRow>
        <FormGroup>
          <FormLabel>Tanggal</FormLabel>
          <FormInput type="text" value={report.date.toLocaleDateString('id-ID')} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Tipe Kelas</FormLabel>
          <FormInput type="text" value={data.class_type || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Kategori</FormLabel>
          <FormInput type="text" value={report.category} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Jenis Ketidakhadiran</FormLabel>
          <FormInput type="text" value={data.absent_type || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>NIS</FormLabel>
          <FormInput type="text" value={report.nis} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Nama Siswa</FormLabel>
          <FormInput type="text" value={report.studentName} readOnly />
        </FormGroup>
      </FormRow>
      
      {data.pelanggaran && (
        <FormRow>
          <FormGroup>
            <FormLabel>Jenis Pelanggaran</FormLabel>
            <FormInput type="text" value={data.pelanggaran.description || '-'} readOnly />
          </FormGroup>
          <FormGroup>
            <FormLabel>Level Pelanggaran</FormLabel>
            <FormInput type="text" value={data.pelanggaran.level || '-'} readOnly />
          </FormGroup>
        </FormRow>
      )}
      
      <FormRow>
        <FormGroup>
          <FormLabel>Section</FormLabel>
          <FormInput type="text" value={data.section || '-'} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Shift</FormLabel>
          <FormInput type="text" value={data.shift || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Total Jam</FormLabel>
          <FormInput type="text" value={formatDecimalValue(data.total_hour)} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Total Jam Kersos</FormLabel>
          <FormInput type="text" value={formatDecimalValue(data.total_hour_kersos)} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Poin Apresiasi</FormLabel>
          <FormInput type="text" value={formatDecimalValue(data.appreciation_point)} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Guru</FormLabel>
          <FormInput type="text" value={data.teacher ? data.teacher.name : '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      {data.additional_consequence && (
        <FormRow>
          <FormGroup fullWidth>
            <FormLabel>Konsekuensi Tambahan</FormLabel>
            <FormTextarea value={data.additional_consequence} readOnly />
          </FormGroup>
        </FormRow>
      )}
      
      <FormRow>
        <FormGroup fullWidth>
          <FormLabel>Keterangan</FormLabel>
          <FormTextarea value={report.keterangan} readOnly />
        </FormGroup>
      </FormRow>
      
      {data.students && data.students.length > 1 && (
        <FormRow>
          <FormGroup fullWidth>
            <FormLabel>Daftar Siswa Terlibat</FormLabel>
            <StudentList>
              {data.students.map((student, index) => (
                <StudentItem key={student.id || index}>
                  {student.nis} - {student.name} ({student.class || 'Kelas tidak diketahui'})
                </StudentItem>
              ))}
            </StudentList>
          </FormGroup>
        </FormRow>
      )}
      
      <FormRow>
        <FormGroup>
          <FormLabel>Status</FormLabel>
          <StatusBadge status={report.status}>{report.statusText}</StatusBadge>
        </FormGroup>
        <FormGroup>
          <FormLabel>Tanggal Keputusan</FormLabel>
          <FormInput type="text" value={data.decision_timestamp ? new Date(data.decision_timestamp).toLocaleDateString('id-ID') : '-'} readOnly />
        </FormGroup>
      </FormRow>
    </FormContent>
  );
};

// Konseling Detail Form Component
const KonselingDetailForm = ({ report }) => {
  const data = report.rawData;
  
  return (
    <FormContent>
      <FormRow>
        <FormGroup>
          <FormLabel>Tanggal</FormLabel>
          <FormInput type="text" value={report.date.toLocaleDateString('id-ID')} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Waktu</FormLabel>
          <FormInput type="text" value={data.time || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>NIS</FormLabel>
          <FormInput type="text" value={report.nis} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Nama Siswa</FormLabel>
          <FormInput type="text" value={report.studentName} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Jenis Konseling</FormLabel>
          <FormInput type="text" value={data.type || '-'} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Tempat</FormLabel>
          <FormInput type="text" value={data.location || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup fullWidth>
          <FormLabel>Catatan</FormLabel>
          <FormTextarea value={report.keterangan} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Status</FormLabel>
          <StatusBadge status={report.status}>{report.statusText}</StatusBadge>
        </FormGroup>
      </FormRow>
    </FormContent>
  );
};

// Lembur Detail Form Component
const LemburDetailForm = ({ report }) => {
  const data = report.rawData;

  return (
    <FormContent>
      <FormRow>
        <FormGroup>
          <FormLabel>Tanggal Pengajuan</FormLabel>
          <FormInput type="text" value={report.date.toLocaleDateString('id-ID')} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Nama Siswa</FormLabel>
          <FormInput type="text" value={report.studentName} readOnly />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <FormLabel>NIS</FormLabel>
          <FormInput type="text" value={report.nis} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Kelas</FormLabel>
          <FormInput type="text" value={data.student ? data.student.class : '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Tanggal Lembur</FormLabel>
          <FormInput type="text" value={data.overtime_date ? new Date(data.overtime_date).toLocaleDateString('id-ID') : '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup>
          <FormLabel>Jam Mulai</FormLabel>
          <FormInput type="text" value={data.start_time || '-'} readOnly />
        </FormGroup>
        <FormGroup>
          <FormLabel>Jam Selesai</FormLabel>
          <FormInput type="text" value={data.finish_time || '-'} readOnly />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup fullWidth>
          <FormLabel>Alasan Lembur</FormLabel>
          <FormTextarea value={data.purpose || '-'} readOnly />
        </FormGroup>
      </FormRow>
      
      <FormRow>
        <FormGroup fullWidth>
          <FormLabel>Jenis Pekerjaan</FormLabel>
          <FormTextarea value={data.type_or_work || '-'} readOnly />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <FormLabel>Guru Bengkel</FormLabel>
          <FormInput type="text" value={data.teacher_name || '-'} readOnly />
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <FormLabel>Status</FormLabel>
          <StatusBadge status={report.status}>{report.statusText}</StatusBadge>
        </FormGroup>
        <FormGroup>
          <FormLabel>Tanggal Keputusan</FormLabel>
          <FormInput type="text" value={data.decision_timestamp ? new Date(data.decision_timestamp).toLocaleDateString('id-ID') : '-'} readOnly />
        </FormGroup>
      </FormRow>
    </FormContent>
  );
};

const RekapLaporanPage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [viewingHistoricalReport, setViewingHistoricalReport] = useState(null);
  const [historicalReports, setHistoricalReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('all'); // 'all', 'kts', 'konseling', or 'lembur'
  
  // Fetch reports from API
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get user data and token from localStorage
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const classType = userData.class_type || '';
        
        if (!token) {
          setError('Authentication token not found. Please login again.');
          setLoading(false);
          return;
        }
        
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        
        // Fetch KTS reports
        const ktsResponse = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/kts-reports`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!ktsResponse.ok) {
          throw new Error('Failed to fetch KTS reports');
        }
        
        const ktsData = await ktsResponse.json();
        
        // Fetch Konseling reports
        const konselingResponse = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/teacher/konseling`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!konselingResponse.ok) {
          throw new Error('Failed to fetch Konseling reports');
        }
        
        const konselingData = await konselingResponse.json();
        
        let lemburReports = [];
        // Fetch Lembur reports only if the teacher is a 'praktek' teacher
        if (classType === 'praktek') {
          const lemburResponse = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/api/teacher/overtime-reports`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!lemburResponse.ok) {
            // It's possible for a praktek teacher to not have access, so we don't throw an error
            // but log it and continue.
            console.error('Could not fetch Lembur reports:', lemburResponse.statusText);
          } else {
            const lemburData = await lemburResponse.json();
            lemburReports = (lemburData.data || []).map(report => ({
                id: report.id,
                date: new Date(report.created_at),
                type: 'Lembur',
                category: 'Laporan Lembur',
                nis: report.student ? report.student.nis : '-',
                studentName: report.student ? report.student.name : 'Unknown Student',
                keterangan: report.purpose || '-',
                status: mapKtsStatusToFrontend(report.request_status || 'menunggu'),
                statusText: getStatusText(mapKtsStatusToFrontend(report.request_status || 'menunggu')),
                rawData: report
            }));
          }
        }
        
        // Process KTS reports
        // The KTS API returns a paginated response with data property containing paginated data
        const ktsReports = (ktsData.data && ktsData.data.data) 
          ? ktsData.data.data.map(report => ({
              id: report.id,
              date: new Date(report.created_at),
              type: 'KTS',
              category: report.category === 'kompensasi' ? 'Kompensasi' : 'Ketidakhadiran',
              nis: report.students && report.students[0] ? report.students[0].nis : '-',
              studentName: report.students && report.students[0] ? report.students[0].name : 'Unknown Student',
              keterangan: (report.category === 'kompensasi' && report.pelanggaran) ? report.pelanggaran.description : (report.description || '-'),
              status: mapKtsStatusToFrontend(report.request_status || 'menunggu'),
              statusText: getStatusText(mapKtsStatusToFrontend(report.request_status || 'menunggu')),
              rawData: report
            }))
          : [];
        
        // Process Konseling reports
        // The Konseling API returns an array of konseling records directly in the data property
        const konselingReports = konselingData.data 
          ? konselingData.data.map(item => ({
              id: item.id,
              date: new Date(item.date),
              type: 'Konseling',
              keterangan: item.notes,
              nis: item.student?.nis || (item.studentInfo && item.studentInfo.length > 0 ? item.studentInfo[0].nis : '-'),
              studentName: item.student?.name || (item.studentInfo && item.studentInfo.length > 0 ? item.studentInfo[0].name : 'Nama Tidak Ditemukan'),
              status: item.request_status || 'menunggu',
              statusText: getStatusText(item.request_status || 'menunggu'),
              rawData: {
                ...item,
                time: item.time || item.preferred_time || item.preferredTime,
                student: item.student || (item.studentInfo && item.studentInfo.length > 0 ? {
                  name: item.studentInfo[0].name,
                  nis: item.studentInfo[0].nis,
                  class: item.studentInfo[0].kelas
                } : null)
              }
            }))
          : [];
        
        // Combine and sort reports by date (newest first)
        const combinedReports = [...ktsReports, ...konselingReports, ...lemburReports].sort((a, b) => b.date - a.date);
        
        setHistoricalReports(combinedReports);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to fetch reports. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchReports();
  }, []);
  
  // Helper function to map KTS status from database to frontend
  const mapKtsStatusToFrontend = (dbStatus) => {
    switch (dbStatus) {
      case 'pending':
        return 'menunggu';
      case 'approved':
        return 'disetujui';
      case 'rejected':
        return 'ditolak';
      default:
        return dbStatus;
    }
  };
  
  const mapKonselingStatusToFrontend = (dbStatus) => {
    // This is a placeholder. Adjust according to your actual status mapping for Konseling.
    switch (dbStatus) {
      case 'pending':
        return 'menunggu';
      case 'approved':
        return 'disetujui';
      case 'rejected':
        return 'ditolak';
      case 'completed':
        return 'selesai';
      default:
        return dbStatus;
    }
  };
  
  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'menunggu':
        return 'Menunggu';
      case 'disetujui':
        return 'Disetujui';
      case 'ditolak':
        return 'Ditolak';
      case 'selesai':
        return 'Selesai';
      default:
        return 'Menunggu';
    }
  };
  
  // Handle view report detail
  const handleViewHistoricalReport = (reportId) => {
    const report = historicalReports.find(r => r.id === reportId);
    if (report) {
      setViewingHistoricalReport(report);
    }
  };
  
  // Filter reports by type
  const filteredReports = historicalReports.filter(report => {
    if (reportType === 'all') return true;
    return report.type.toLowerCase() === reportType.toLowerCase();
  });
  
  return (
    <PageContainer darkMode={darkMode}>
      <PageHeader>
        <h1>Rekap Laporan</h1>
        <p>Lihat dan kelola semua laporan yang telah dibuat</p>
      </PageHeader>
      
      {viewingHistoricalReport ? (
        <FormContainer darkMode={darkMode}>
          <FormWrapper>
            <h2>
              {viewingHistoricalReport.type === 'KTS' && 'Detail Laporan KTS'}
              {viewingHistoricalReport.type === 'Konseling' && 'Detail Sesi Konseling'}
              {viewingHistoricalReport.type === 'Lembur' && 'Detail Laporan Lembur'}
            </h2>
            
            {viewingHistoricalReport.type === 'KTS' ? (
              <KTSDetailForm report={viewingHistoricalReport} />
            ) : viewingHistoricalReport.type === 'Lembur' ? (
              <LemburDetailForm report={viewingHistoricalReport} />
            ) : (
              <KonselingDetailForm report={viewingHistoricalReport} />
            )}
            
            <ButtonGroup>
              <Button onClick={() => setViewingHistoricalReport(null)}>Kembali</Button>
            </ButtonGroup>
          </FormWrapper>
        </FormContainer>
      ) : (
        <HistorySection>
          <SectionHeader>
            <h2>Riwayat Laporan</h2>
            
            <TypeToggleContainer>
              <TypeToggleButtonGroup>
                <TypeToggleButton 
                  active={reportType === 'all'} 
                  onClick={() => setReportType('all')}
                >
                  Semua
                </TypeToggleButton>
                <TypeToggleButton 
                  active={reportType === 'kts'} 
                  onClick={() => setReportType('kts')}
                >
                  KTS
                </TypeToggleButton>
                <TypeToggleButton 
                  active={reportType === 'konseling'} 
                  onClick={() => setReportType('konseling')}
                >
                  Konseling
                </TypeToggleButton>
                {JSON.parse(localStorage.getItem('user') || '{}').class_type === 'praktek' && (
                  <TypeToggleButton
                    active={reportType === 'lembur'}
                    onClick={() => setReportType('lembur')}
                  >
                    Lembur
                  </TypeToggleButton>
                )}
              </TypeToggleButtonGroup>
            </TypeToggleContainer>
          </SectionHeader>
          
          {loading ? (
            <LoadingState>
              <p>Loading reports...</p>
            </LoadingState>
          ) : error ? (
            <ErrorState>
              <p>{error}</p>
            </ErrorState>
          ) : filteredReports.length === 0 ? (
            <EmptyState>
              <p>Tidak ada laporan yang ditemukan.</p>
            </EmptyState>
          ) : (
            <RequestTable darkMode={darkMode}>
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Tipe</th>
                  <th>Kategori</th>
                  <th>NIS</th>
                  <th>Nama Siswa</th>
                  <th>Keterangan</th>
                  <th>Status</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={`${report.type}-${report.id}`}>
                    <td>{report.date.toLocaleDateString('id-ID')}</td>
                    <td>{report.type}</td>
                    <td>{report.category}</td>
                    <td>{report.nis}</td>
                    <td>{report.studentName}</td>
                    <td>{report.keterangan}</td>
                    <td><StatusBadge status={report.status}>{report.statusText}</StatusBadge></td>
                    <td><ActionButton onClick={() => handleViewHistoricalReport(report.id)}>Lihat</ActionButton></td>
                  </tr>
                ))}
              </tbody>
            </RequestTable>
          )}
        </HistorySection>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 2rem;
  color: ${props => props.darkMode ? 'var(--text-light)' : 'var(--text-dark)'};
  background-color: ${props => props.darkMode ? 'var(--background-dark)' : 'var(--background-light)'};
  min-height: 100vh;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }
  
  p {
    color: var(--text-light);
  }
`;

const FormContainer = styled.div`
  background-color: ${props => props.darkMode ? 'var(--card-bg-dark)' : 'var(--card-bg-light)'};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s, box-shadow 0.3s;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const FormWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const TypeToggleContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TypeToggleButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--background-hover)'};
  }
`;

const HistorySection = styled.div`
  margin-top: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const RequestTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background-color: ${props => props.darkMode ? 'var(--card-bg-dark)' : 'var(--card-bg-light)'};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
  }
  
  th {
    background-color: ${props => props.darkMode ? 'var(--background-dark)' : 'var(--background-light)'};
    font-weight: 600;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: 1024px) {
    display: block;
    overflow-x: auto;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.status) {
      case 'approved':
        return 'var(--success-light)';
      case 'rejected':
        return 'var(--danger-light)';
      default:
        return 'var(--warning-light)';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'approved':
        return 'var(--success)';
      case 'rejected':
        return 'var(--danger)';
      default:
        return 'var(--warning)';
    }
  }};
`;

const ActionButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: var(--primary);
  color: white;
  border: none;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  background-color: var(--card-bg-light);
  border-radius: 8px;
  box-shadow: var(--box-shadow);
`;

const ErrorState = styled.div`
  padding: 2rem;
  text-align: center;
  background-color: var(--danger-light);
  color: var(--danger);
  border-radius: 8px;
  box-shadow: var(--box-shadow);
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  background-color: var(--card-bg-light);
  border-radius: 8px;
  box-shadow: var(--box-shadow);
`;

// Form Styled Components
const FormContent = styled.div`
  margin: 1.5rem 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  flex: ${props => props.fullWidth ? '1 0 100%' : '1'};
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text-light);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--input-bg);
  color: var(--text-dark);
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  &:read-only {
    background-color: var(--background-light);
    cursor: default;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--input-bg);
  color: var(--text-dark);
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  &:read-only {
    background-color: var(--background-light);
    cursor: default;
  }
`;

const StudentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 1rem;
`;

const StudentItem = styled.li`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.875rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

export default RekapLaporanPage;
